import OfflineImage from "components/OfflineImage/OfflineImage";
import React, { useContext, useState } from "react";
import styles from "./SearchResultsCarouselItem.module.scss";
import { OperatingHours } from "utils";
import { useCalculateOperatingHours } from "hooks/useCalculateOperatingHours";
import { wrapOpeningHours } from "components/FeatureInformation/FeatureInformation";
import getDistance from "utils/getDistance";
import { Icon } from "@livingmap/hippo";
import { Label } from "@livingmap/hippo";
import classNames from "classnames";
import { MapContext } from "components/Map/Map";

import placeholderImageDark from "./img/placeholder_dark.png";
import placeholderImageLight from "./img/placeholder_light.png";

export interface SearchResultsCarouselItemProps {
  imageUrl?: string;
  title?: string;
  category?: string;
  floor?: string;
  address?: string;
  operatingHours: OperatingHours;
  kilometres?: number;
  showNearestLabel?: boolean;
  onClick?: () => void;
}

const SearchResultsCarouselItem: React.FC<SearchResultsCarouselItemProps> = ({
  imageUrl,
  title,
  category,
  floor,
  address,
  operatingHours,
  kilometres,
  showNearestLabel,
  onClick,
}) => {
  const { controlTheme, distanceUnit = "metric" } = useContext(MapContext);
  const { statusCopy } = useCalculateOperatingHours(operatingHours);
  const distance = kilometres ? getDistance(kilometres, distanceUnit) : null;

  const [clicked, setClicked] = useState(false);

  const handleClick = () => {
    setClicked(true);
    if (onClick) onClick();
    setTimeout(() => setClicked(false), 200);
  };

  return (
    <div
      className={classNames(styles.container, { [styles.clicked]: clicked })}
      onClick={handleClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {imageUrl ? (
        <OfflineImage theme={controlTheme} src={imageUrl} alt={title} />
      ) : (
        <OfflineImage
          theme={controlTheme}
          src={
            controlTheme === "dark"
              ? placeholderImageDark
              : placeholderImageLight
          }
          alt="placeholder"
        />
      )}
      {showNearestLabel && (
        <Label
          dataQA="nearest-label"
          className={styles.nearestLabel}
          text="NEAREST"
          type="location"
          // eslint-disable-next-line react/style-prop-object
          style="filled"
        />
      )}

      <div className={styles.content}>
        {title && <h2 className={styles.title}>{title}</h2>}
        <div className={styles.info}>
          {category && <span className={styles.category}>{category}</span>}
          {(floor || address) && (
            <span className={styles.floorAndAddress}>
              {floor}
              {floor && address && " · "}
              {address}
            </span>
          )}
          <span
            className={styles.operatingHours}
            dangerouslySetInnerHTML={{
              __html: wrapOpeningHours(statusCopy || ""),
            }}
          />
          {kilometres && (
            <span className={styles.kilometres}>
              <Icon dataQA="distance-icon" size={18} name="directions_walk" />{" "}
              {distance}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchResultsCarouselItem;
